import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

/**
 * The unsaved-answer navigation guard (PX-W6.5, AC-2 / INV-9).
 *
 * React-router's declarative `<Routes>` mode has no `useBlocker`, so the
 * guard is built from the two navigation channels that exist:
 *
 *  1. IN-APP anchors — a document-level CAPTURE-phase `click` listener
 *     intercepts `a[href]` (exactly what react-router's `<Link>` renders),
 *     `preventDefault`s it and parks the href. The parked href renders the
 *     confirm dialog; "Discard" clears the dirty flag and navigates there,
 *     "Keep editing" drops it.
 *  2. Reload / close — a `beforeunload` listener while dirty (the browser's
 *     own confirm; no custom dialog is possible there).
 *
 * Capture phase is load-bearing: react-router's own listener sits on the
 * document too, and only an earlier (capturing) listener can prevent the
 * default navigation before it runs.
 *
 * @param dirty - Whether the guarded surface has unsaved edits.
 * @returns `pendingHref` (the intercepted destination while the dialog is
 *   open — null when closed), `confirmDiscard`, `keepEditing` and
 *   `guardActive` (whether the listener is armed).
 */
export function useUnsavedAnswerGuard(dirty: boolean): {
  pendingHref: string | null
  confirmDiscard: () => void
  keepEditing: () => void
  guardActive: boolean
} {
  const navigate = useNavigate()
  const [pendingHref, setPendingHref] = useState<string | null>(null)

  useEffect(() => {
    if (!dirty) return
    const onClickCapture = (event: MouseEvent): void => {
      const target = event.target
      if (!(target instanceof Element)) return
      const anchor = target.closest('a[href]')
      if (anchor === null) return
      const href = anchor.getAttribute('href')
      if (href === null || href === '') return
      event.preventDefault()
      event.stopPropagation()
      setPendingHref(href)
    }
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault()
      // Legacy requirement for the browser's own confirm.
      event.returnValue = ''
    }
    document.addEventListener('click', onClickCapture, true)
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      document.removeEventListener('click', onClickCapture, true)
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [dirty])

  const confirmDiscard = useCallback((): void => {
    const to = pendingHref
    setPendingHref(null)
    if (to !== null) navigate(to)
  }, [navigate, pendingHref])

  const keepEditing = useCallback((): void => {
    setPendingHref(null)
  }, [])

  return { pendingHref, confirmDiscard, keepEditing, guardActive: dirty }
}
